import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import Parse from 'parse';
import { useGetIdentity } from 'react-admin';
import CustomPagination from '../../components/CustomPagination';

// Human-readable field label mapping
const FIELD_LABELS = {
  username: 'Username',
  name: 'Name',
  email: 'Email',
  phone: 'Phone',
  isActive: 'Status',
  operatorLogo: 'Logo',
  password: 'Password',
  domains: 'Domains',
  domainName: 'Domain Name',
  isPrimary: 'Primary Domain',
  operatorKey: 'Operator Key',
  role: 'Role'
};

const humanLabel = (field) =>
  FIELD_LABELS[field] ||
  field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

const humanValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Active' : 'Inactive';
  if (typeof value === 'object') {
    if (value.__type === 'Pointer') return value.objectId || '—';
    if (value.fileName) return value.fileName;
    if (value.name) return value.name;
    if (value.objectId) return value.objectId;
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) return value.length === 0 ? '—' : value.join(', ');
  return String(value);
};

const AUDIT_LOG_CLASS = 'RgsActivityLog';

// Renders the before → after diff for changed fields only
const DetailsCell = ({ details, action }) => {
  const changedFields = details?.changedFields || [];

  // For "create" actions, show payload fields as "new values"
  const isCreate = String(action || '')
    .toLowerCase()
    .includes('create');
  const isDelete = String(action || '')
    .toLowerCase()
    .includes('delete');

  if (isDelete && changedFields.length === 0) {
    const username = details?.username || details?.summary?.split(' ').pop() || '';
    return (
      <Typography sx={{ fontSize: '12px', color: 'rgba(255,100,100,0.9)', fontStyle: 'italic' }}>
        {username ? `Operator "${username}" was deleted` : 'Record deleted'}
      </Typography>
    );
  }

  if (isCreate) {
    const payload = details?.payload || {};
    const entries = Object.entries(payload).filter(
      ([, v]) => v !== undefined && v !== null && v !== ''
    );
    if (entries.length === 0) {
      return <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)' }}>—</Typography>;
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {entries.map(([field, val]) => (
          <Box
            key={field}
            sx={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' }}
          >
            <Typography
              sx={{
                fontSize: '11px',
                color: 'rgba(255,255,255,0.45)',
                minWidth: '72px',
                flexShrink: 0
              }}
            >
              {humanLabel(field)}
            </Typography>
            <Chip
              label={humanValue(val)}
              size="small"
              sx={{
                height: '18px',
                fontSize: '11px',
                backgroundColor: 'rgba(76,175,80,0.15)',
                color: '#81c784',
                '& .MuiChip-label': { px: '6px' }
              }}
            />
          </Box>
        ))}
      </Box>
    );
  }

  if (changedFields.length === 0) {
    return (
      <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)' }}>
        No changes recorded
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {changedFields.map(({ field, from, to }) => (
        <Box key={field} sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {/* Field label */}
          <Typography
            sx={{
              fontSize: '10px',
              color: 'rgba(255,255,255,0.4)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            {humanLabel(field)}
          </Typography>
          {/* Before → After */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            {/* Before */}
            <Chip
              label={humanValue(from)}
              size="small"
              sx={{
                height: '18px',
                fontSize: '11px',
                backgroundColor: 'rgba(244,67,54,0.12)',
                color: '#ef9a9a',
                textDecoration: 'line-through',
                '& .MuiChip-label': { px: '6px' }
              }}
            />
            {/* Arrow */}
            <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>→</Typography>
            {/* After */}
            <Chip
              label={humanValue(to)}
              size="small"
              sx={{
                height: '18px',
                fontSize: '11px',
                backgroundColor: 'rgba(76,175,80,0.15)',
                color: '#81c784',
                '& .MuiChip-label': { px: '6px' }
              }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        return formatValue(JSON.parse(trimmed));
      } catch {
        return value;
      }
    }
    return value;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    return `[${value.map((item) => formatValue(item)).join(', ')}]`;
  }
  if (typeof value === 'object') {
    if (value.__type === 'Pointer') return value.objectId || '-';
    if (value.fileName) return value.fileName;
    if (value.name) return value.name;
    if (value.objectId) return value.objectId;
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const formatAction = (action) => {
  const value = String(action || '-').trim();
  const normalized = value.toLowerCase();

  if (normalized.includes('delete') || normalized === 'delete') return 'Delete';
  if (normalized.includes('create') || normalized === 'create') return 'Create';
  if (normalized.includes('update') || normalized === 'update') return 'Update';

  return '-';
};

const getActionColor = (action) => {
  const normalized = String(action || '').toLowerCase();
  if (normalized.includes('delete')) return { bg: 'rgba(244,67,54,0.15)', color: '#ef9a9a' };
  if (normalized.includes('create')) return { bg: 'rgba(76,175,80,0.15)', color: '#81c784' };
  return { bg: 'rgba(255,193,7,0.15)', color: '#FFC107' };
};

const formatSection = (section, targetType, details) => {
  const value = section || details?.section || details?.resource || targetType || '-';

  const mapping = {
    operatorGames: 'Operator Games',
    'operator-games': 'Operator Games',
    operatormanagement: 'Operator Management',
    'operator-management': 'Operator Management',
    operators: 'Operator Management',
    GameCatalogue: 'Game Records',
    gameCatalogueSwitch: 'Game Records',
    GameBetConfig: 'Game Bet Levels',
    adminActivityLogs: 'Admin Activity Logs',
    'admin-activity-logs': 'Admin Activity Logs'
  };

  const normalized = String(value).replace(/\s+/g, '').trim();
  return mapping[value] || mapping[normalized] || String(value);
};

const formatTargetType = (targetType, section, details) => {
  const value = targetType || details?.targetType || section || '-';
  const mapping = {
    _User: 'Operator',
    GameCatalogue: 'Game Record',
    gameCatalogueSwitch: 'Game Record',
    GameBetConfig: 'Game Bet Config',
    OperatorGameConfig: 'Operator Game Config',
    OperatorDomain: 'Operator Domain'
  };

  return mapping[value] || String(value);
};

const getTargetDisplayName = (log, operatorNameMap, gameNameMap) => {
  const targetType = String(log.targetType || '');
  const details = log.details || {};
  const payload = details.payload || {};

  if (targetType === '_User') {
    return operatorNameMap.get(log.targetId) || log.targetId || '-';
  }

  const gameId = log.targetId || details.gameId || details.gameSlug || details.slug;
  const gameName =
    gameNameMap.get(gameId) ||
    details.name ||
    details.gameName ||
    payload.name ||
    payload.gameName ||
    payload.slug ||
    details.gameSlug ||
    details.slug ||
    gameId ||
    '-';

  if (
    [
      'RgsGame',
      'GameCatalogue',
      'gameCatalogueSwitch',
      'OperatorGameConfig',
      'GameBetConfig'
    ].includes(targetType)
  ) {
    return gameName;
  }

  return (
    details.name ||
    details.gameName ||
    payload.name ||
    payload.gameName ||
    log.targetName ||
    gameName
  );
};

const AdminActivityLogs = () => {
  const { data: identity, isLoading } = useGetIdentity();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (isLoading) return;

    if (identity?.role !== 'Super-User') {
      setLoading(false);
      return;
    }

    const fetchLogs = async () => {
      setLoading(true);
      try {
        Parse.masterKey = process.env.REACT_APP_MASTER_KEY;

        const buildQuery = () => {
          const query = new Parse.Query(AUDIT_LOG_CLASS);
          query.equalTo('actorRole', 'Admin');
          query.descending('createdAt');
          return query;
        };

        const listQuery = buildQuery();
        listQuery.skip((page - 1) * perPage);
        listQuery.limit(perPage);

        const [items, count] = await Promise.all([
          listQuery.find({ useMasterKey: true }),
          buildQuery().count({ useMasterKey: true })
        ]);

        const targetIds = [
          ...new Set(
            items
              .filter((log) => log.get('targetType') === '_User' && log.get('targetId'))
              .map((log) => log.get('targetId'))
          )
        ];

        let targetNameMap = new Map();
        if (targetIds.length > 0) {
          const targetQuery = new Parse.Query(Parse.User);
          targetQuery.containedIn('objectId', targetIds);
          targetQuery.select('username', 'name');

          const targetUsers = await targetQuery.find({ useMasterKey: true });
          targetNameMap = new Map(
            targetUsers.map((user) => [
              user.id,
              user.get('name') || user.get('username') || user.id
            ])
          );
        }

        const gameIds = [
          ...new Set(
            items
              .map((log) => {
                const targetType = log.get('targetType');
                if (
                  !['RgsGame', 'GameCatalogue', 'GameBetConfig', 'gameCatalogueSwitch'].includes(
                    targetType
                  )
                )
                  return null;
                return log.get('targetId') || log.get('details')?.targetId || null;
              })
              .filter(Boolean)
          )
        ];

        let gameNameMap = new Map();
        if (gameIds.length > 0) {
          const gameQuery = new Parse.Query('GameCatalogue');
          gameQuery.containedIn('objectId', gameIds);
          gameQuery.select('name', 'gameName', 'slug');

          const games = await gameQuery.find({ useMasterKey: true });
          gameNameMap = new Map(
            games.map((game) => [
              game.id,
              game.get('name') || game.get('gameName') || game.get('slug') || game.id
            ])
          );
        }

        const mapped = items.map((log) => ({
          id: log.id,
          action: log.get('action'),
          actorId: log.get('actorId'),
          actorName: log.get('actorName'),
          section: log.get('section'),
          targetType: log.get('targetType'),
          targetId: log.get('targetId'),
          targetName: getTargetDisplayName(
            {
              targetType: log.get('targetType'),
              targetId: log.get('targetId'),
              details: log.get('details'),
              targetName:
                log.get('targetType') === '_User' ? targetNameMap.get(log.get('targetId')) : null
            },
            targetNameMap,
            gameNameMap
          ),
          details: log.get('details'),
          createdAt: log.get('createdAt')
        }));

        // Hide update entries that recorded zero changes
        const filtered = mapped.filter((log) => {
          const actionNorm = String(log.action || '').toLowerCase();
          const isUpdate = actionNorm.includes('update');
          if (!isUpdate) return true; // always show create / delete
          const changedFields = log.details?.changedFields || [];
          return changedFields.length > 0;
        });

        setLogs(filtered);
        setTotal(count);
      } catch (error) {
        console.error('Failed to load admin activity logs:', error);
        setLogs([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [identity?.role, isLoading, page, perPage]);

  if (isLoading) {
    return null;
  }

  if (identity?.role !== 'Super-User') {
    return <Box sx={{ p: 3, color: 'rgba(255,255,255,0.7)' }}>Access denied. Super-User only.</Box>;
  }

  const HEADERS = ['Action', 'Actor', 'Section', 'Target', 'Details', 'Created At'];

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
          Tracks actions performed by Admin users.
        </Typography>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: '#242424',
          borderRadius: 2,
          boxShadow: 'none',
          overflowX: 'auto'
        }}
      >
        <Table sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow>
              {HEADERS.map((header) => (
                <TableCell
                  key={header}
                  sx={{
                    color: '#fff',
                    fontWeight: 'bold',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={HEADERS.length}
                  align="center"
                  sx={{ py: 4, borderBottom: 'none' }}
                >
                  <CircularProgress sx={{ color: '#FFC107' }} />
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={HEADERS.length}
                  align="center"
                  sx={{ py: 4, color: 'rgba(255,255,255,0.5)', borderBottom: 'none' }}
                >
                  No admin activity logs found.
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => {
                const actionColors = getActionColor(log.action);
                return (
                  <TableRow
                    key={log.id}
                    sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.02)' } }}
                  >
                    {/* Action */}
                    <TableCell
                      sx={{
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        verticalAlign: 'top',
                        py: 1.5
                      }}
                    >
                      <Chip
                        label={formatAction(log.action)}
                        size="small"
                        sx={{ backgroundColor: actionColors.bg, color: actionColors.color }}
                      />
                    </TableCell>

                    {/* Actor */}
                    <TableCell
                      sx={{
                        color: '#fff',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        verticalAlign: 'top',
                        py: 1.5
                      }}
                    >
                      <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                        {formatValue(log.actorName || log.actorId)}
                      </Typography>
                    </TableCell>

                    {/* Section */}
                    <TableCell
                      sx={{
                        color: '#fff',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        verticalAlign: 'top',
                        py: 1.5
                      }}
                    >
                      <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                        {formatValue(formatSection(log.section, log.targetType, log.details))}
                      </Typography>
                    </TableCell>

                    {/* Target */}
                    <TableCell
                      sx={{
                        color: '#fff',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        verticalAlign: 'top',
                        py: 1.5
                      }}
                    >
                      <Typography sx={{ fontSize: '13px' }}>
                        {formatValue(log.targetName)}
                      </Typography>
                    </TableCell>

                    {/* Details — before/after changed fields */}
                    <TableCell
                      sx={{
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        verticalAlign: 'top',
                        py: 1.5,
                        minWidth: '260px',
                        maxWidth: '360px'
                      }}
                    >
                      <DetailsCell details={log.details} action={log.action} />
                    </TableCell>

                    {/* Created At */}
                    <TableCell
                      sx={{
                        color: '#fff',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        verticalAlign: 'top',
                        py: 1.5,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {log.createdAt ? new Date(log.createdAt).toLocaleString() : '-'}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {!loading && logs.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CustomPagination
              page={page}
              perPage={perPage}
              total={total}
              setPage={setPage}
              setPerPage={setPerPage}
            />
          </Box>
        )}
      </TableContainer>
    </Box>
  );
};

export default AdminActivityLogs;
